import React, { useRef } from "react";
import {
    Drawer,
    Box,
    Typography,
    Chip,
    Button,
    IconButton,
    alpha,
    Tooltip,
} from "@mui/material";
import {
    Close,
    Print,
    TableBar,
    AccessTime,
    StickyNote2,
    CheckCircle,
    Cancel,
} from "@mui/icons-material";
import { useReactToPrint } from "react-to-print";
import moment from "moment";
import KitchenBill from "../POS/components/OrderCheckout/KitchenBill";
import ImageLoader from "../../components/ImageLoader";

// ── Palette ──────────────────────────────────────────────────────────────────
const TEXT_PRIMARY = "#1e293b";
const TEXT_SECONDARY = "#526070";

// ── Config maps ───────────────────────────────────────────────────────────────
const ORDER_TYPE_MAP = {
    "dine-in": { accent: "#6366f1", label: "Dine-In" },
    takeaway: { accent: "#10b981", label: "Takeaway" },
    delivery: { accent: "#e8900a", label: "Delivery" },
};

const STATUS_CONFIG = {
    pending: { color: "#d97706", bg: "#fef3c7", label: "PENDING" },
    confirmed: { color: "#6366f1", bg: "#e0e7ff", label: "CONFIRMED" },
    preparing: { color: "#FF6600", bg: "#FFF0E6", label: "PREPARING" },
    ready: { color: "#059669", bg: "#d1fae5", label: "READY" },
    served: { color: "#6366f1", bg: "#e0e7ff", label: "SERVED" },
    completed: { color: "#6b7280", bg: "#f3f4f6", label: "DONE" },
    cancelled: { color: "#dc2626", bg: "#fee2e2", label: "CANCELLED" },
};

const ITEM_STATUS_BASE = {
    pending: { color: "#d97706", bg: "#fef3c7", label: "Waiting" },
    preparing: { color: "#FF6600", bg: "#FFF0E6", label: "Preparing" },
    ready: { color: "#059669", bg: "#d1fae5", label: "Ready" },
    served: { color: "#6366f1", bg: "#e0e7ff", label: "Served" },
    completed: { color: "#6b7280", bg: "#f3f4f6", label: "Completed" },
    cancelled: { color: "#dc2626", bg: "#fee2e2", label: "Cancelled" },
};

const ORDER_NEXT_MAP = {
    pending: { next: "preparing", label: "Start Preparing", bg: "#FF6600" },
    confirmed: { next: "preparing", label: "Start Preparing", bg: "#FF6600" },
    preparing: { next: "ready", label: "Mark as Ready", bg: "#059669" },
    ready: { next: "served", label: "Mark as Served", bg: "#6366f1" },
};

const getItemCfg = (itemStatus, order, isWaiter) => {
    const isPaid = order?.paymentStatus === "paid";
    const base = ITEM_STATUS_BASE[itemStatus] || ITEM_STATUS_BASE.pending;
    if (isWaiter) {
        if (itemStatus === "ready")
            return { ...base, next: "served", nextLabel: "Mark as Served", nextBg: "#6366f1" };
        if (itemStatus === "served" && isPaid)
            return { ...base, next: "completed", nextLabel: "Mark as Completed", nextBg: "#6b7280" };
        return { ...base, next: null, nextLabel: null, nextBg: null };
    }
    if (itemStatus === "pending")
        return { ...base, next: "preparing", nextLabel: "Start Preparing", nextBg: "#FF6600" };
    if (itemStatus === "preparing")
        return { ...base, next: "ready", nextLabel: "Mark as Ready", nextBg: "#10b981" };
    return { ...base, next: null, nextLabel: null, nextBg: null };
};

const formatElapsed = (dateString) => {
    if (!dateString) return "—";
    const mins = Math.floor((Date.now() - new Date(dateString)) / 60000);
    if (mins < 1) return "Just now";
    if (mins < 60) return `${mins}m ago`;
    return `${Math.floor(mins / 60)}h ${mins % 60}m ago`;
};

/**
 * KDSOrderDrawer
 * Slide-in right panel opened when a kitchen card is tapped.
 * Shows full order details, per-item status actions, and a reprint button.
 *
 * Props:
 *   open                — boolean
 *   order               — order object | null (auto-updates from parent state)
 *   onClose             — () => void
 *   isWaiter            — boolean
 *   companyInfo         — company object (for kitchen bill print)
 *   onStatusUpdate      — (orderId, newStatus, order) => void
 *   onItemStatusUpdate  — (itemId,  newStatus, order) => void
 *   onRequestCancelOrder — (order) => void   opens confirm dialog in parent
 *   onRequestCancelItem  — (item, order) => void
 */
const KDSOrderDrawer = ({
    open,
    order,
    onClose,
    isWaiter,
    companyInfo,
    onStatusUpdate,
    onItemStatusUpdate,
    onRequestCancelOrder,
    onRequestCancelItem,
}) => {
    const kitchenPrintRef = useRef();
    const handleKitchenPrint = useReactToPrint({
        contentRef: kitchenPrintRef,
        documentTitle: `Kitchen Order - ${order?.orderNumber || "ORDER"}`,
    });

    if (!order) return null;

    const typeCfg = ORDER_TYPE_MAP[order.orderType] || ORDER_TYPE_MAP.takeaway;
    const accentColor = typeCfg.accent;
    const statusCfg = STATUS_CONFIG[order.status] || STATUS_CONFIG.pending;
    const orderNum = order.orderNumber?.split("-").pop() || "N/A";
    const isMultiItem = (order.OrderItems?.length || 0) > 1;
    const isCancelable = !["cancelled", "completed", "served"].includes(order.status);
    const orderStep = ORDER_NEXT_MAP[order.status];
    const showOrderCTA = !!orderStep && (
        isWaiter
            ? order.status === "ready"
            : !isMultiItem && ["pending", "confirmed", "preparing"].includes(order.status)
    );

    return (
        <Drawer
            anchor="right"
            open={open}
            onClose={onClose}
            PaperProps={{
                sx: {
                    width: { xs: "100vw", sm: 500 },
                    bgcolor: "#f1f5fb",
                    display: "flex",
                    flexDirection: "column",
                },
            }}
        >
            {/* ── HEADER ──────────────────────────────────────────────────────── */}
            <Box
                sx={{
                    px: 2.5,
                    py: 2,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    flexShrink: 0,
                    background: `linear-gradient(145deg, ${alpha(accentColor, 0.12)} 0%, ${alpha(accentColor, 0.04)} 100%)`,
                    borderBottom: `1px solid ${alpha(accentColor, 0.18)}`,
                }}
            >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <Box
                        sx={{
                            width: 44, height: 44, borderRadius: "12px",
                            bgcolor: alpha(accentColor, 0.14),
                            display: "flex", alignItems: "center", justifyContent: "center",
                        }}
                    >
                        <Typography sx={{ fontWeight: 600, fontSize: "1.05rem", color: accentColor }}>
                            #{orderNum}
                        </Typography>
                    </Box>
                    <Box>
                        <Typography sx={{ fontWeight: 600, fontSize: "1.05rem", color: TEXT_PRIMARY, lineHeight: 1.2 }}>
                            Order #{orderNum}
                        </Typography>
                        <Typography sx={{ fontSize: "0.78rem", color: TEXT_SECONDARY, fontWeight: 500 }}>
                            {typeCfg.label} · {formatElapsed(order.createdAt)}
                        </Typography>
                    </Box>
                </Box>

                <Box sx={{ display: "flex", gap: 1 }}>
                    <Tooltip title="Reprint Kitchen Ticket">
                        <IconButton
                            onClick={handleKitchenPrint}
                            sx={{
                                bgcolor: alpha(accentColor, 0.1),
                                color: accentColor,
                                borderRadius: "10px",
                                "&:hover": { bgcolor: alpha(accentColor, 0.22) },
                            }}
                        >
                            <Print sx={{ fontSize: 20 }} />
                        </IconButton>
                    </Tooltip>
                    <IconButton
                        onClick={onClose}
                        sx={{
                            bgcolor: alpha("#526070", 0.08),
                            borderRadius: "10px",
                            "&:hover": { bgcolor: alpha("#526070", 0.16) },
                        }}
                    >
                        <Close sx={{ fontSize: 20 }} />
                    </IconButton>
                </Box>
            </Box>

            {/* ── SCROLLABLE BODY ─────────────────────────────────────────────── */}
            <Box sx={{ flex: 1, overflowY: "auto", px: 2.5, py: 2.5, display: "flex", flexDirection: "column", gap: 2 }}>

                {/* Order details card */}
                <Box
                    sx={{
                        bgcolor: "#fff",
                        borderRadius: "14px",
                        border: `1px solid ${alpha(accentColor, 0.18)}`,
                        overflow: "hidden",
                    }}
                >
                    {/* 2-column grid — each cell equal width */}
                    <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr" }}>

                        {/* Status */}
                        <Box sx={{ px: 2, py: 1.25, borderRight: "1px solid #e2e8f0", borderBottom: "1px solid #e2e8f0" }}>
                            <Typography sx={{ fontSize: "0.65rem", fontWeight: 600, color: TEXT_SECONDARY, textTransform: "uppercase", letterSpacing: 0.5, mb: 0.5 }}>Status</Typography>
                            <Chip
                                label={statusCfg.label}
                                size="small"
                                sx={{ fontWeight: 600, fontSize: "0.7rem", bgcolor: statusCfg.bg, color: statusCfg.color, "& .MuiChip-label": { px: 1 } }}
                            />
                        </Box>

                        {/* Placed At */}
                        <Box sx={{ px: 2, py: 1.25, borderBottom: "1px solid #e2e8f0", display: "flex", alignItems: "flex-start", gap: 0.75 }}>
                            <AccessTime sx={{ fontSize: 14, color: TEXT_SECONDARY, mt: 0.2 }} />
                            <Box>
                                <Typography sx={{ fontSize: "0.65rem", fontWeight: 600, color: TEXT_SECONDARY, textTransform: "uppercase", letterSpacing: 0.5, mb: 0.3 }}>Placed At</Typography>
                                <Typography sx={{ fontSize: "0.88rem", fontWeight: 600, color: TEXT_PRIMARY, lineHeight: 1.2 }}>
                                    {moment(order.createdAt).format("hh:mm A")}
                                </Typography>
                            </Box>
                        </Box>

                        {/* Elapsed */}
                        <Box sx={{
                            px: 2, py: 1.25,
                            borderRight: "1px solid #e2e8f0",
                            ...(order.Table || order.customerName ? {} : {}),
                        }}>
                            <Typography sx={{ fontSize: "0.65rem", fontWeight: 600, color: TEXT_SECONDARY, textTransform: "uppercase", letterSpacing: 0.5, mb: 0.3 }}>Elapsed</Typography>
                            <Typography sx={{ fontSize: "0.88rem", fontWeight: 600, color: TEXT_PRIMARY, lineHeight: 1.2 }}>
                                {formatElapsed(order.createdAt)}
                            </Typography>
                        </Box>

                        {/* Table — or empty cell to keep grid balanced */}
                        {order.Table ? (
                            <Box sx={{ px: 2, py: 1.25, display: "flex", alignItems: "flex-start", gap: 0.75 }}>
                                <TableBar sx={{ fontSize: 15, color: accentColor, mt: 0.2 }} />
                                <Box>
                                    <Typography sx={{ fontSize: "0.65rem", fontWeight: 600, color: TEXT_SECONDARY, textTransform: "uppercase", letterSpacing: 0.5, mb: 0.3 }}>Table</Typography>
                                    <Typography sx={{ fontSize: "0.88rem", fontWeight: 600, color: accentColor, lineHeight: 1.2 }}>
                                        {order.Table.tableNumber}{order.Table.location ? ` · ${order.Table.location}` : ""}
                                    </Typography>
                                </Box>
                            </Box>
                        ) : (
                            <Box />
                        )}

                        {/* Customer — spans full width if present */}
                        {order.customerName && (
                            <Box sx={{ px: 2, py: 1.25, gridColumn: "1 / -1", borderTop: "1px solid #e2e8f0" }}>
                                <Typography sx={{ fontSize: "0.65rem", fontWeight: 600, color: TEXT_SECONDARY, textTransform: "uppercase", letterSpacing: 0.5, mb: 0.3 }}>Customer</Typography>
                                <Typography sx={{ fontSize: "0.88rem", fontWeight: 600, color: TEXT_PRIMARY, lineHeight: 1.2 }}>
                                    {order.customerName}
                                </Typography>
                            </Box>
                        )}
                    </Box>
                </Box>

                {/* Order note */}
                {order.specialInstructions && (
                    <Box
                        sx={{
                            display: "flex", alignItems: "flex-start", gap: 1.25,
                            px: 1.75, py: 1.25, bgcolor: "#fffbeb", borderRadius: "12px",
                            border: "1px solid #fde68a",
                        }}
                    >
                        <StickyNote2 sx={{ fontSize: 18, color: "#d97706", mt: 0.1, flexShrink: 0 }} />
                        <Box>
                            <Typography sx={{ fontSize: "0.68rem", fontWeight: 600, color: "#92400e", textTransform: "uppercase", letterSpacing: 0.5, mb: 0.3 }}>Order Note</Typography>
                            <Typography sx={{ fontSize: "0.9rem", color: "#92400e", fontWeight: 600, lineHeight: 1.45 }}>
                                {order.specialInstructions}
                            </Typography>
                        </Box>
                    </Box>
                )}

                {/* Items */}
                <Box>
                    <Typography sx={{ fontSize: "0.72rem", fontWeight: 600, color: TEXT_SECONDARY, textTransform: "uppercase", letterSpacing: 0.5, mb: 1.25 }}>
                        Items ({order.OrderItems?.length || 0})
                    </Typography>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
                        {order.OrderItems?.map((item, idx) => {
                            const cfg = getItemCfg(item.status, order, isWaiter);
                            const isDone = ["cancelled", "completed"].includes(item.status);
                            return (
                                <Box
                                    key={idx}
                                    sx={{
                                        bgcolor: "#fff",
                                        borderRadius: "14px",
                                        border: `1px solid ${alpha(accentColor, 0.15)}`,
                                        overflow: "hidden",
                                        opacity: isDone ? 0.55 : 1,
                                        transition: "opacity 0.2s",
                                    }}
                                >
                                    {/* Main row */}
                                    <Box sx={{ display: "flex", alignItems: "stretch" }}>
                                        {/* Item image + qty overlay */}
                                        <Box
                                            sx={{
                                                width: 72, flexShrink: 0,
                                                position: "relative",
                                                bgcolor: alpha(accentColor, 0.08),
                                                overflow: "hidden",
                                            }}
                                        >
                                            {item.MenuItem?.image ? (
                                                <ImageLoader
                                                    imagePath={item.MenuItem.image}
                                                    width={72}
                                                    height={72}
                                                    alt={item.MenuItem?.name || "Item"}
                                                    rounded={false}
                                                    borderRadius={0}
                                                />
                                            ) : (
                                                <Box
                                                    sx={{
                                                        width: 72, height: 72,
                                                        display: "flex", alignItems: "center", justifyContent: "center",
                                                        bgcolor: alpha(accentColor, 0.12),
                                                    }}
                                                >
                                                    <Typography sx={{ fontWeight: 600, fontSize: "1.35rem", color: accentColor, lineHeight: 1 }}>
                                                        {item.quantity}×
                                                    </Typography>
                                                </Box>
                                            )}
                                            {/* Qty badge overlaid bottom-left */}
                                            {item.MenuItem?.image && (
                                                <Box
                                                    sx={{
                                                        position: "absolute", bottom: 4, left: 4,
                                                        bgcolor: alpha("#000", 0.62),
                                                        color: "#fff",
                                                        borderRadius: "6px",
                                                        px: 0.75, py: 0.2,
                                                        fontSize: "0.78rem",
                                                        fontWeight: 600,
                                                        lineHeight: 1.4,
                                                        backdropFilter: "blur(2px)",
                                                    }}
                                                >
                                                    ×{item.quantity}
                                                </Box>
                                            )}
                                        </Box>
                                        {/* Details */}
                                        <Box sx={{ flex: 1, px: 1.5, py: 1.35 }}>
                                            <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1 }}>
                                                <Typography sx={{ fontWeight: 600, fontSize: "1rem", color: TEXT_PRIMARY, lineHeight: 1.3 }}>
                                                    {item.MenuItem?.name || "Item"}
                                                </Typography>
                                                {/* Per-item status badge — only for multi-item orders */}
                                                {isMultiItem && (
                                                    <Chip
                                                        label={cfg.label}
                                                        size="small"
                                                        sx={{
                                                            fontWeight: 600, fontSize: "0.69rem", height: 22,
                                                            bgcolor: cfg.bg, color: cfg.color,
                                                            "& .MuiChip-label": { px: 1 }, flexShrink: 0,
                                                        }}
                                                    />
                                                )}
                                            </Box>
                                            {item.ItemVariation && (
                                                <Typography sx={{ fontSize: "0.82rem", color: TEXT_SECONDARY, mt: 0.3, fontWeight: 500 }}>
                                                    {item.ItemVariation.name}
                                                </Typography>
                                            )}
                                            {item.OrderItemModifiers?.length > 0 && (
                                                <Box sx={{ mt: 0.65, display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                                                    {item.OrderItemModifiers.map((mod, mIdx) => (
                                                        <Typography key={mIdx} sx={{ fontSize: "0.75rem", fontWeight: 600, color: TEXT_SECONDARY, bgcolor: "#dde4ef", px: 0.85, py: 0.3, borderRadius: "6px" }}>
                                                            + {mod.ModifierOption?.name}
                                                        </Typography>
                                                    ))}
                                                </Box>
                                            )}
                                        </Box>
                                    </Box>

                                    {/* Special instructions banner */}
                                    {item.specialInstructions && (
                                        <Box sx={{ px: 1.5, py: 0.9, bgcolor: "#fff3e0", borderTop: "1px dashed #fb923c", display: "flex", alignItems: "flex-start", gap: 0.75 }}>
                                            <StickyNote2 sx={{ fontSize: 14, color: "#ea580c", mt: 0.1, flexShrink: 0 }} />
                                            <Typography sx={{ fontSize: "0.85rem", color: "#b45309", fontWeight: 600, lineHeight: 1.4 }}>
                                                {item.specialInstructions}
                                            </Typography>
                                        </Box>
                                    )}

                                    {/* Item action buttons — multi-item + non-terminal only */}
                                    {isMultiItem && !isDone && (
                                        <Box sx={{ px: 1.5, pb: 1.25, pt: 0.85, borderTop: `1px solid ${alpha(accentColor, 0.12)}`, display: "flex", gap: 1 }}>
                                            {cfg.next && (
                                                <Button
                                                    fullWidth
                                                    size="small"
                                                    onClick={() => onItemStatusUpdate(item.id, cfg.next, order)}
                                                    sx={{
                                                        fontWeight: 600, fontSize: "0.84rem", py: 1, borderRadius: "10px",
                                                        bgcolor: alpha(cfg.nextBg, 0.12), color: cfg.nextBg, textTransform: "none",
                                                        border: `1.5px solid ${alpha(cfg.nextBg, 0.4)}`, boxShadow: "none",
                                                        "&:hover": { bgcolor: cfg.nextBg, color: "#fff", border: `1.5px solid ${cfg.nextBg}`, boxShadow: `0 3px 10px ${alpha(cfg.nextBg, 0.35)}` },
                                                        "&:active": { transform: "scale(0.98)" },
                                                    }}
                                                >
                                                    {cfg.nextLabel}
                                                </Button>
                                            )}
                                            <Button
                                                size="small"
                                                onClick={() => onRequestCancelItem(item, order)}
                                                sx={{
                                                    fontWeight: 600, fontSize: "0.84rem", py: 1, px: 1.75, borderRadius: "10px",
                                                    bgcolor: alpha("#dc2626", 0.09), color: "#dc2626", textTransform: "none",
                                                    border: `1.5px solid ${alpha("#dc2626", 0.32)}`, boxShadow: "none", flexShrink: 0,
                                                    "&:hover": { bgcolor: "#dc2626", color: "#fff", border: `1.5px solid #dc2626` },
                                                    "&:active": { transform: "scale(0.98)" },
                                                }}
                                            >
                                                <Cancel sx={{ fontSize: 15 }} />
                                            </Button>
                                        </Box>
                                    )}
                                </Box>
                            );
                        })}
                    </Box>
                </Box>
            </Box>

            {/* ── STICKY FOOTER: order-level actions ─────────────────────────── */}
            {(showOrderCTA || isCancelable) && (
                <Box
                    sx={{
                        px: 2.5, py: 2,
                        borderTop: "1px solid #cdd5e0",
                        bgcolor: "#fff",
                        display: "flex",
                        gap: 1.25,
                        flexShrink: 0,
                    }}
                >
                    {showOrderCTA && orderStep && (
                        <Button
                            fullWidth
                            startIcon={<CheckCircle sx={{ fontSize: 16 }} />}
                            onClick={() => onStatusUpdate(order.id, orderStep.next, order)}
                            sx={{
                                fontWeight: 600, fontSize: "0.96rem", py: 1.5, borderRadius: "12px",
                                bgcolor: alpha(orderStep.bg, 0.14), color: orderStep.bg, textTransform: "none",
                                border: `1.5px solid ${alpha(orderStep.bg, 0.35)}`, boxShadow: "none",
                                "&:hover": { bgcolor: orderStep.bg, color: "#fff", border: `1.5px solid ${orderStep.bg}`, boxShadow: `0 4px 14px ${alpha(orderStep.bg, 0.3)}` },
                                "&:active": { transform: "scale(0.99)" },
                            }}
                        >
                            {orderStep.label}
                        </Button>
                    )}
                    {isCancelable && (
                        <Button
                            {...(!showOrderCTA ? { fullWidth: true } : {})}
                            onClick={() => onRequestCancelOrder(order)}
                            sx={{
                                fontWeight: 600, fontSize: "0.96rem", py: 1.5,
                                ...(showOrderCTA ? { px: 2.5, flexShrink: 0 } : {}),
                                borderRadius: "12px",
                                bgcolor: alpha("#dc2626", 0.09), color: "#dc2626", textTransform: "none",
                                border: `1.5px solid ${alpha("#dc2626", 0.32)}`, boxShadow: "none",
                                "&:hover": { bgcolor: "#dc2626", color: "#fff", border: `1.5px solid #dc2626`, boxShadow: `0 4px 14px ${alpha("#dc2626", 0.3)}` },
                                "&:active": { transform: "scale(0.99)" },
                            }}
                        >
                            {showOrderCTA
                                ? <Cancel sx={{ fontSize: 18 }} />
                                : <><Cancel sx={{ fontSize: 16, mr: 0.75 }} />Cancel Order</>
                            }
                        </Button>
                    )}
                </Box>
            )}

            {/* ── Hidden kitchen bill for printing ── */}
            <Box sx={{ display: "none" }}>
                <Box ref={kitchenPrintRef}>
                    <KitchenBill orderResult={order} companyInfo={companyInfo} />
                </Box>
            </Box>
        </Drawer>
    );
};

export default KDSOrderDrawer;
